import { AbschnittDisplayModel, AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode, AenderungsantragDisplayModel, AenderungsantragDisplayModelFormGroup, AnfrageDisplayModel, GlobalEnumerationsAceTypaenderungsantrag } from '@/api/model-index';
import { AenderungsantragService } from '@/api/service-index';
import { Constants } from '@/constants';
import { AnfrageNavigationFormComponent, MoveDirection } from '@/core/components/anfrage-navigation-form/anfrage-navigation-form.component';
import { ConfirmationDialogAction, ConfirmationDialogComponent, ConfirmationDialogResult } from '@/core/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { EnumValue } from '@/core/models/enumvalue.model';
import { AnfrageTransformerService } from '@/core/services/anfrage-transformer.service';
import { ComponentService } from '@/core/services/component.service';
import { I18nService } from '@/core/services/i18nService';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { NavigationService } from '@/core/services/navigation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { SectionService } from '@/core/services/section.service';
import { UnfinishedHttpMethodsService } from '@/core/services/unfinished-http-methods.service';
import { Component, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from '@progress/kendo-angular-dialog';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { GridDataResult, RemoveEvent } from '@progress/kendo-angular-grid';
import { NotificationService } from '@progress/kendo-angular-notification';
import { SortDescriptor } from '@progress/kendo-data-query';

@Component({
    selector: 'aenderungsantrag-overview',
    templateUrl: './aenderungsantrag-overview.component.html',
    styleUrls: ['./aenderungsantrag-overview.component.scss'],
    standalone: false
})
export class AenderungsantragOverviewComponent extends AnfrageNavigationFormComponent {
  public form: AenderungsantragDisplayModelFormGroup;
  public aenderungsantraege: AenderungsantragDisplayModel[] = [];
  public anfrage: AnfrageDisplayModel;
  public gridView: GridDataResult;
  public isFormOpen: boolean = false;
  protected abschnitt: AbschnittDisplayModel | null = null;

  @ViewChild("antragsTypDropdown")
  public antragsTypDropdown: ComboBoxComponent;

  public get formGroup(): FormGroup<any> {
    return this.form;
  }

  public sort: SortDescriptor[] = [
    { field: "createdOn", dir: "asc" }
  ];

  constructor(
    protected sectionService: SectionService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected portalContextService: PortalContextService,
    protected aenderungsantragService: AenderungsantragService,
    protected loadingAnimationService: LoadingAnimationService,
    protected unfinishedHttpMethodsService: UnfinishedHttpMethodsService,
    protected componentService: ComponentService,
    protected languageService: I18nService,
    protected navigationService: NavigationService,
    protected anfrageTransformerService: AnfrageTransformerService,
    protected dialogService: DialogService,
    protected notificationService: NotificationService
  ) {
    super(sectionService, router, route, portalContextService);

    this.form = AenderungsantragDisplayModelFormGroup.Instance;
    this.anfrage = this.route.snapshot.data.anfrage;
    this.aenderungsantraege = this.route.snapshot.data.aenderungsantraege;
    this.abschnitt = this.sectionService.current();

    this.initGridView();
  }

  public formatTyp(typ: GlobalEnumerationsAceTypaenderungsantrag): string {
    return this.languageService.getEnumValue(GlobalEnumerationsAceTypaenderungsantrag, typ)?.Description ?? '';
  }

  public formatStatus(status: AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode): string {
    return this.languageService.getEnumValue(AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode, status)?.Description ?? '';
  }

  public canBeDeleted(aenderungsantrag: AenderungsantragDisplayModel): boolean {
    return aenderungsantrag.status === AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.Entwurf;
  }

  public get typItems(): EnumValue[] {
    const allTypes = this.languageService.getEnumValues(GlobalEnumerationsAceTypaenderungsantrag);

    return allTypes.filter(t => this.abschnitt?.erlaubteAenderungsantragsTypen?.includes(t.Value));
  }

  override async canDeactivate(): Promise<boolean> {
    if (!this.form.valid && this.isFormOpen) {
      return this.openUnsavedChangesDialog();
    }
    return true;
  }

  protected initGridView() {
    this.gridView = {
      data: this.aenderungsantraege.sort((a, b) => b.createdOn!.localeCompare(a.createdOn!)),
      total: this.aenderungsantraege.length,
    };
  }

  public createHandler() {
    if (this.typItems.length === 0) {
      this.notificationService.show({
        closable: true,
        content: this.languageService.instant("aenderungsantrag.currently_not_possible"),
        animation: { type: "fade", duration: 500 },
        position: { horizontal: "center", vertical: "bottom" },
        type: { style: "error", icon: true },
      });
      return;
    }

    this.form = AenderungsantragDisplayModelFormGroup.Instance;
    this.form.Typ.addValidators(Validators.required);

    this.isFormOpen = true;

    //Focus the dropdown in the next render cycle
    setTimeout(() => {
      this.antragsTypDropdown?.focus();
    });
  }

  public cancelForm() {
    this.isFormOpen = false;
  }

  protected removeHandler({ dataItem }: RemoveEvent): void {
    let deleteDialogRef = this.dialogService.open({
      content: this.componentService.get(ConfirmationDialogComponent),
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      title: this.languageService.instant("aenderungsantrag.dialog.titleDelete")
    });
    let dialogComponent: ConfirmationDialogComponent = deleteDialogRef.content.instance;

    dialogComponent.content = this.languageService.instant("aenderungsantrag.dialog.contentDelete");

    deleteDialogRef.result.subscribe(result => this.onDeleteDialogClose(<ConfirmationDialogResult>result, dataItem));
  }

  protected onDeleteDialogClose(result: ConfirmationDialogResult, dataItem: AenderungsantragDisplayModel) {
    if (result.action == ConfirmationDialogAction.Confirm) {
      const subscription = this.aenderungsantragService.apiAenderungsantragDeleteIdDelete({ id: dataItem.id! }).subscribe(async () => {
        const index = this.aenderungsantraege.findIndex(item => item.id === dataItem.id);
        if (index !== -1) {
          this.aenderungsantraege.splice(index, 1);
          this.portalContextService.Aenderungsantraege = this.aenderungsantraege;
          this.initGridView();

          const transformedAnfrage = await this.anfrageTransformerService.transform(this.portalContextService.CurrentAnfrage);
          this.portalContextService.CurrentAnfrage = transformedAnfrage;
        }
      });

      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  public createAenderungsantrag(): void {
    const aenderungsantrag: AenderungsantragDisplayModel = this.form.value;
    aenderungsantrag.createdOn = new Date().toJSON();
    aenderungsantrag.status = AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.Entwurf;
    this.isFormOpen = false;

    let subscription = this.aenderungsantragService.apiAenderungsantragCreateProjektIdPost({ projektId: this.anfrage.id, body: aenderungsantrag.typ }).subscribe(async (response) => {
      aenderungsantrag.id = response;
      this.aenderungsantraege.push(aenderungsantrag);
      this.portalContextService.Aenderungsantraege = this.aenderungsantraege;

      this.initGridView();

      const transformedAnfrage = await this.anfrageTransformerService.transform(this.portalContextService.CurrentAnfrage);
      this.portalContextService.CurrentAnfrage = transformedAnfrage;

      await this.openAenderungsantrag(response);
    })

    this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    this.unfinishedHttpMethodsService.addSubscription(subscription);
  }

  protected async openAenderungsantrag(id: string) {
    await this.sectionService.jumpToSection(this.router, this.route, id);
  }

  protected save(shouldSave: boolean, moveDirection: MoveDirection): void {
    // Nothing to save on that form 
    this.move(moveDirection);

    if (shouldSave && this.form.valid && this.isFormOpen) {
      this.createAenderungsantrag();
    }
  }
}
